import { I18n } from '@/i18n/I18n';
import { create } from 'zustand';
import { AssistantMessage, AssistantStreamEvent } from '@/types/reportAssistantTypes';
import { InputSchemaParams, InputSchemaSummary, InputSchemaVersionParams, SchemaUser } from '@/backend/access/schema/types';
import { CreateSchemaRequest, UpdateSchemaRequest } from '@/types/schemaTypes';
import { SchemaFrontendController } from '../controllers/schemaFrontendController';
import { useReportBundleStore } from './ReportBundleStore';

/** A schema as its page edits it: the draft, its published versions newest first, and the bundles that read it. */
export interface SchemaEntry {
  schema: InputSchemaParams;
  /** which draft this is, sent back with a save so it never overwrites a draft saved since */
  draftHash?: string;
  versions: InputSchemaVersionParams[];
  usedBy: SchemaUser[];
}

interface SchemaStore {
  /** every schema; null until fetched */
  schemas: InputSchemaSummary[] | null;
  schemasById: Record<string, SchemaEntry>;

  fetchSchemas: () => Promise<void>;
  fetchSchema: (id: string) => Promise<SchemaEntry | null>;
  createSchema: (data: CreateSchemaRequest) => Promise<InputSchemaParams | null>;
  updateMeta: (id: string, data: UpdateSchemaRequest) => Promise<boolean>;
  /**
   * store the draft as given; what is wrong with it comes back on the schema. `draftHash`: the draft it was
   * edited from; when the stored draft moved on since, nothing is saved, the entry is read again, and `conflict` comes back
   */
  saveDraft: (id: string, source: string, draftHash?: string) => Promise<{ entry: SchemaEntry } | { conflict: true } | null>;
  /** publish the stored draft; `draftHash`: the draft the author was shown, refused when it was saved elsewhere since (the entry is read again) */
  publish: (id: string, note?: string, draftHash?: string) => Promise<InputSchemaVersionParams | null>;
  deleteSchema: (id: string) => Promise<boolean>;
  /** one reply of the schema's assistant, streamed; throws with why when the server would not start one */
  streamAssistantReply: (id: string, messages: AssistantMessage[], onEvent: (event: AssistantStreamEvent) => void, signal: AbortSignal) => Promise<void>;
  reset: () => void;
}

const initialState = {
  schemas: null as InputSchemaSummary[] | null,
  schemasById: {} as Record<string, SchemaEntry>,
};

export const useSchemaStore = create<SchemaStore>((set, get) => ({
  ...initialState,

  fetchSchemas: async () => {
    const response = await new SchemaFrontendController().list();
    if (response.status === 200) set({ schemas: response.schemas });
  },

  fetchSchema: async (id) => {
    const response = await new SchemaFrontendController().read(id);
    if (response.status !== 200 || !response.schema) return null;
    const entry: SchemaEntry = { schema: response.schema, draftHash: response.draftHash, versions: response.versions ?? [], usedBy: response.usedBy ?? [] };
    set({ schemasById: { ...get().schemasById, [id]: entry } });
    return entry;
  },

  createSchema: async (data) => {
    const response = await new SchemaFrontendController().create(data);
    if (response.status !== 201 || !response.schema) return null;
    set({ schemasById: { ...get().schemasById, [response.schema.id]: { schema: response.schema, draftHash: response.draftHash, versions: [], usedBy: [] } }, schemas: null });
    return response.schema;
  },

  updateMeta: async (id, data) => {
    const response = await new SchemaFrontendController().update(id, data);
    if (response.status !== 200 || !response.schema) return false;
    const current = get().schemasById[id];
    if (current) set({ schemasById: { ...get().schemasById, [id]: { ...current, schema: response.schema } } });
    set({ schemas: null });
    return true;
  },

  saveDraft: async (id, source, draftHash) => {
    const response = await new SchemaFrontendController().saveDraft(id, { source, ...(draftHash ? { draftHash } : {}) });
    if (response.status === 422 && draftHash) {
      // refused, perhaps for being stale: the draft as it is now, for the page to offer instead of what it was editing
      const now = await get().fetchSchema(id);
      return now?.draftHash && now.draftHash !== draftHash ? { conflict: true } : null;
    }
    if (response.status !== 200 || !response.schema) return null;
    const current = get().schemasById[id];
    const entry: SchemaEntry = { schema: response.schema, draftHash: response.draftHash, versions: current?.versions ?? [], usedBy: current?.usedBy ?? [] };
    set({ schemasById: { ...get().schemasById, [id]: entry }, schemas: null });
    // the bundles that follow the draft carry it among their files
    useReportBundleStore.getState().forgetBundles(entry.usedBy.map((u) => u.reportBundleId));
    return { entry };
  },

  publish: async (id, note, draftHash) => {
    const response = await new SchemaFrontendController().publish(id, { note, ...(draftHash ? { draftHash } : {}) });
    if (response.status === 422 && draftHash) await get().fetchSchema(id);
    if (response.status !== 201 || !response.version) return null;
    const entry = await get().fetchSchema(id);
    set({ schemas: null });
    // what the bundles show of the schema (its latest version, whether the draft is published) changed
    useReportBundleStore.getState().forgetBundles((entry ?? get().schemasById[id])?.usedBy.map((u) => u.reportBundleId) ?? []);
    return response.version;
  },

  streamAssistantReply: async (id, messages, onEvent, signal) => {
    const refused = await new SchemaFrontendController().assistantReply(id, messages, onEvent, signal);
    if (refused) throw new Error(refused.errors?.[0]?.message || I18n.get('reportAssistantRequestFailed', { message: `HTTP ${refused.status}` }));
  },

  deleteSchema: async (id) => {
    const response = await new SchemaFrontendController().delete(id);
    if (response.status !== 200) return false;
    const { [id]: _gone, ...rest } = get().schemasById;
    set({ schemasById: rest, schemas: get().schemas?.filter((s) => s.id !== id) ?? null });
    return true;
  },

  reset: () => set({ ...initialState }),
}));
